import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function SupplierLogin() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const { login, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError(t('validation.required')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError(t('validation.invalidEmail')); return; }
    if (!password) { setError(t('validation.required')); return; }
    setLoading(true); setError('');
    try {
      const result = await login(email, password, rememberMe);
      if (result.success) navigate('/app/supplier/dashboard');
      else setError(result.error || 'Invalid email or password');
    } catch (err) {
      setError('Invalid email or password');
    } finally { setLoading(false); }
  };

  const fillTest = () => { setEmail('demo_supplier@expostand.com'); setPassword('expo1234'); setError(''); };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* ── Left brand panel ── */}
      <div className="hidden lg:flex lg:w-[58%] relative flex-col justify-between p-12 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1E0A3C 0%, #4A1D96 50%, #7C3AED 100%)' }}>
        {/* Background blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10" style={{ background: '#C4B5FD', filter: 'blur(80px)', transform: 'translate(30%, -30%)' }} />
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-10" style={{ background: '#A78BFA', filter: 'blur(60px)', transform: 'translate(-30%, 30%)' }} />
          <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
            <defs><pattern id="grid-s" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/></pattern></defs>
            <rect width="100%" height="100%" fill="url(#grid-s)" />
          </svg>
        </div>

        {/* Two-column content layout */}
        <div className="relative z-10 flex gap-12 flex-1">
          {/* Left: brand + copy */}
          <div className="flex-1 flex flex-col justify-center">
            <a href="/" className="flex items-center gap-3 mb-12">
              <img src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" alt="EXPO STAND" className="w-10 h-9 opacity-90" />
              <div>
                <div className="text-white font-bold text-lg leading-tight">EXPO STAND</div>
                <div className="text-purple-300 text-xs">Global Exhibition Network</div>
              </div>
            </a>

            <h1 className="text-5xl font-bold text-white leading-tight mb-4">
              Supplier<br /><span className="text-purple-300">Portal</span>
            </h1>
            <p className="text-purple-200 text-base mb-10 max-w-xs">
              Showcase your products and services to the exhibition industry. Connect, grow, and scale globally.
            </p>

            <div className="space-y-4">
              {[
                { icon: '◈', text: 'List 1,000+ products in your catalog' },
                { icon: '◎', text: 'Direct communication with verified buyers' },
                { icon: '◉', text: 'Access to 46-country exhibition market' },
                { icon: '◐', text: 'Secure payments & order management' },
              ].map(item => (
                <div key={item.text} className="flex items-center gap-3">
                  <span className="text-purple-400 text-lg w-6 shrink-0">{item.icon}</span>
                  <span className="text-purple-100 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: floating stat cards */}
          <div className="w-56 flex flex-col justify-center gap-4 shrink-0">
            {[
              { val: '1,000+', lbl: 'Products', icon: '◈', color: 'from-purple-500/20 to-purple-600/10' },
              { val: '46', lbl: 'Countries', icon: '◎', color: 'from-violet-500/20 to-violet-600/10' },
              { val: '98%', lbl: 'On-Time Delivery', icon: '◉', color: 'from-fuchsia-500/20 to-fuchsia-600/10' },
              { val: '3.2K', lbl: 'Buyers Reached', icon: '◐', color: 'from-purple-400/20 to-purple-500/10' },
            ].map(card => (
              <div key={card.lbl}
                className={`rounded-2xl border border-white/10 bg-gradient-to-br ${card.color} backdrop-blur-sm p-4`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-purple-300 text-base">{card.icon}</span>
                  <span className="text-white text-xl font-bold">{card.val}</span>
                </div>
                <div className="text-purple-300 text-xs pl-6">{card.lbl}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="relative z-10 pt-8 border-t border-purple-700/50">
          <p className="text-purple-400 text-xs">"Your trusted marketplace for the global exhibition industry"</p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-white relative">
        {/* Language switcher */}
        <div className="absolute top-5 right-5">
          <LanguageSwitcher variant="dark" />
        </div>

        {/* Mobile logo */}
        <a href="/" className="flex items-center gap-2 mb-8 lg:hidden">
          <img src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" alt="EXPO STAND" className="w-8 h-7" />
          <span className="font-bold text-gray-800">EXPO STAND</span>
        </a>

        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">{t('login.supplierPortal')}</h2>
            <p className="text-sm text-gray-500 mt-1">{t('login.welcomeBack')}</p>
          </div>

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('login.emailLabel')}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                placeholder={t('login.emailPlaceholder')}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('login.passwordLabel')}</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder={t('login.passwordPlaceholder')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition pr-16" />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-medium">
                  {showPw ? '●' : '○'}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                <input type="checkbox" id="supplier-remember-me" checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="rounded border-gray-300 accent-purple-600 cursor-pointer" />
                <span className="cursor-pointer select-none">{t('login.rememberMe')}</span>
              </label>
              <button type="button" onClick={() => setShowForgot(true)} className="text-xs text-purple-600 hover:underline" data-testid="forgot-password-link">
                {t('login.forgotPassword')}
              </button>
            </div>
            <button type="submit" disabled={loading}
              data-testid="supplier-login-submit"
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-60"
              style={{ background: loading ? '#C4B5FD' : 'linear-gradient(135deg, #4A1D96, #7C3AED)' }}>
              {loading ? t('login.signingIn') : t('login.signInBtn')}
            </button>
          </form>

          <button onClick={fillTest}
            data-testid="supplier-login-fill-test"
            className="mt-4 w-full py-2.5 rounded-xl border border-dashed border-purple-200 text-purple-600 text-xs font-semibold hover:bg-purple-50 transition-colors flex items-center justify-center gap-2">
            ⚡ Demo: demo_supplier@expostand.com
          </button>

          <p className="mt-6 text-center text-xs text-gray-500">
            {t('login.noAccount')}{' '}
            <Link to="/supplier/signup" className="text-purple-600 font-semibold hover:underline">{t('login.registerHere')}</Link>
          </p>
          <p className="mt-3 text-center">
            <a href="/" className="text-xs text-gray-400 hover:text-gray-600">← {t('nav.backToHome')}</a>
          </p>

          <div className="mt-8 pt-6 border-t border-gray-100 flex justify-center gap-4 text-xs text-gray-400">
            <Link to="/exhibitor/login" className="hover:text-gray-600">{t('login.links.exhibitorLogin')}</Link>
            <span>·</span>
            <Link to="/contractor/login" className="hover:text-gray-600">{t('login.links.contractorLogin')}</Link>
            <span>·</span>
            <Link to="/admin-login" className="hover:text-gray-600 text-red-400">{t('login.links.adminLogin')}</Link>
          </div>
        </div>
      </div>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} accentColor="purple" />}
    </div>
  );
}
